import { useState } from "react";
import { Usb, Settings2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  connectQz,
  listPrinters,
  guessLxPrinter,
  printRawEscp,
  QZ_DOWNLOAD_URL,
} from "@/lib/qzPrint";

const STORAGE_KEY = "lx310_printer";

// One-click raw ESC/P printing to the branch's LX-310 via QZ Tray.
// `getBytes` returns the ESC/P Uint8Array to print (or null if not ready).
export default function DirectPrintButton({ getBytes, disabled }) {
  const [printer, setPrinter] = useState(() => localStorage.getItem(STORAGE_KEY) || "");
  const [printers, setPrinters] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [pending, setPending] = useState("");
  const [choice, setChoice] = useState("");

  const qzNotRunning = (e) => {
    toast.error("QZ Tray not detected on this PC", {
      description:
        "Install QZ Tray once (it lets the browser print directly to the LX-310), then reload this page.",
      action: { label: "Download", onClick: () => window.open(QZ_DOWNLOAD_URL, "_blank") },
      duration: 12000,
    });
    console.error("QZ connect failed:", e);
  };

  const doPrint = async (printerName) => {
    const bytes = getBytes();
    if (!bytes) return;
    setPending("printing");
    try {
      await printRawEscp(bytes, printerName);
      toast.success(`Sent to ${printerName}`, {
        description: "Raw ESC/P — the LX-310 prints in its own crisp font.",
      });
    } catch (e) {
      toast.error("Print failed", { description: String(e?.message || e) });
      console.error(e);
    } finally {
      setPending("");
    }
  };

  const openPicker = async () => {
    setPending("connecting");
    try {
      await connectQz();
      const list = await listPrinters();
      setPrinters(list);
      const preset = printer || guessLxPrinter(list) || list[0] || "";
      setChoice(preset);
      setDialogOpen(true);
    } catch (e) {
      qzNotRunning(e);
    } finally {
      setPending("");
    }
  };

  const handlePrint = async () => {
    const bytes = getBytes();
    if (!bytes) return;
    setPending("connecting");
    try {
      await connectQz();
      const list = await listPrinters();
      setPrinters(list);
      // Use saved printer if it still exists; otherwise ask.
      if (printer && list.includes(printer)) {
        setPending("");
        await doPrint(printer);
        return;
      }
      const preset = guessLxPrinter(list) || list[0] || "";
      setChoice(preset);
      setDialogOpen(true);
      setPending("");
    } catch (e) {
      setPending("");
      qzNotRunning(e);
    }
  };

  const saveAndPrint = async () => {
    if (!choice) return;
    localStorage.setItem(STORAGE_KEY, choice);
    setPrinter(choice);
    setDialogOpen(false);
    await doPrint(choice);
  };

  const busy = pending !== "";

  return (
    <>
      <Button
        size="sm"
        onClick={handlePrint}
        disabled={disabled || busy}
        className="gap-2 bg-slate-900 hover:bg-slate-800 disabled:opacity-40 disabled:cursor-not-allowed"
        data-testid="direct-print-button"
        title="Print directly to the EPSON LX-310 (via QZ Tray) — crisp raw ESC/P"
      >
        {pending === "connecting" ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <Usb className="h-4 w-4" />
        )}
        Print to LX-310
      </Button>
      <Button
        variant="outline"
        size="sm"
        onClick={openPicker}
        disabled={busy}
        className="px-2"
        data-testid="direct-print-settings-button"
        title="Choose which printer to use"
      >
        <Settings2 className="h-4 w-4" />
      </Button>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent data-testid="printer-picker-dialog">
          <DialogHeader>
            <DialogTitle>Select the LX-310 printer</DialogTitle>
            <DialogDescription>
              Pick the printer queue connected to your EPSON LX-310. This choice is
              remembered on this PC.
            </DialogDescription>
          </DialogHeader>
          <div
            className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800"
            data-testid="windows-driver-hint"
          >
            <b>Windows tip:</b> if it says printed but nothing comes out, the normal
            EPSON driver is blocking the raw text. Add a printer on the LX-310's USB
            port using the <b>“Generic / Text Only”</b> driver and select that queue
            here. (macOS works with the normal driver.)
          </div>
          {printers.length === 0 ? (
            <p className="text-sm text-slate-500">No printers found. Make sure the LX-310 is installed in Windows/macOS.</p>
          ) : (
            <Select value={choice} onValueChange={setChoice}>
              <SelectTrigger data-testid="printer-select">
                <SelectValue placeholder="Choose a printer" />
              </SelectTrigger>
              <SelectContent>
                {printers.map((p) => (
                  <SelectItem key={p} value={p} data-testid={`printer-option-${p}`}>
                    {p}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} data-testid="printer-picker-cancel">
              Cancel
            </Button>
            <Button
              onClick={saveAndPrint}
              disabled={!choice || pending === "printing"}
              data-testid="printer-picker-save"
              className="bg-slate-900 hover:bg-slate-800"
            >
              {pending === "printing" ? "Printing…" : "Save & Print"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
